// src/pages/Booking.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPropertyById } from '../services/propertyService';
import { createPaymentIntent, confirmBooking } from '../services/paymentService';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

function CheckoutForm({ finalPrice, propertyId, user, form }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    try {
      // Creeaza PaymentIntent in backend
      const paymentIntent = await createPaymentIntent({
        propertyId,
        startDate: form.checkIn,
        endDate: form.checkOut,
        totalPrice: finalPrice,
        rooms: form.rooms,
        roomsPrice: form.roomsPrice,
        mealOptions: form.mealOptions,
        selectedMeal: form.selectedMeal,
        discount: form.discount,
        adults: form.adults,
        children: form.children,
        freeCancellation: form.freeCancellation
      });

      // Confirma plata prin Stripe.js
      const { error, paymentIntent: confirmedIntent } = await stripe.confirmCardPayment(
        paymentIntent.clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: { name: user.name, email: user.email }
          }
        }
      );

      if (error) {
        alert(error.message);
        setLoading(false);
        return;
      }

      // Confirma booking-ul in backend
      await confirmBooking({
        propertyId,
        startDate: form.checkIn,
        endDate: form.checkOut,
        totalPrice: finalPrice,
        paymentIntentId: confirmedIntent.id,
        rooms: form.rooms,
        roomsPrice: form.roomsPrice,
        mealOptions: form.mealOptions,
        selectedMeal: form.selectedMeal,
        discount: form.discount,
        adults: form.adults,
        children: form.children,
        freeCancellation: form.freeCancellation
      });

      alert('Rezervarea și plata au fost finalizate cu succes!');
    } catch (err) {
      console.error(err);
      alert('Eroare la procesarea plății');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <CardElement
        options={{
          style: {
            base: { fontSize: '0.9rem', color: '#000', '::placeholder': { color: '#999' }, fontFamily: 'Sora, sans-serif' }
          }
        }}
      />
      <button
        type="submit"
        disabled={loading}
        style={{
          padding: '0.6rem 1rem',
          backgroundColor: '#006d77',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontWeight: '600'
        }}
      >
        {loading ? 'Se procesează...' : `Plătește ${finalPrice.toFixed(2)} RON`}
      </button>
    </form>
  );
}

export default function Booking() {
  const { user } = useAuth();
  const { id: propertyId } = useParams();
  const navigate = useNavigate(); // 🔹 corect
  const location = useLocation();
  const bookingData = location.state?.bookingData;

  const [property, setProperty] = useState(null);
  const [form, setForm] = useState({
    checkIn: '',
    checkOut: '',
    rooms: {},
    roomsPrice: {},
    roomsAvailable: {},
    mealOptions: {},
    selectedMeal: '',
    discount: 0,
    adults: 1,
    children: 0,
    freeCancellation: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookingData) {
      // Folosim datele trimise din Property.jsx
      setForm({
        checkIn: bookingData.checkIn,
        checkOut: bookingData.checkOut,
        rooms: bookingData.rooms || {},
        roomsPrice: bookingData.roomsPrice || {},
        roomsAvailable: bookingData.roomsAvailable || {},
        mealOptions: bookingData.mealOptions || {},
        selectedMeal: bookingData.selectedMeal || '',
        discount: bookingData.discount || 0,
        adults: bookingData.adults || 1,
        children: bookingData.children || 0,
        freeCancellation: bookingData.freeCancellation || false
      });
      setProperty({
        title: bookingData.propertyTitle,
        location: bookingData.propertyLocation
      });
      setLoading(false);
      return;
    }

    // Daca nu exista bookingData, facem fetch
    const fetchProperty = async () => {
      try {
        const res = await getPropertyById(propertyId);
        setProperty(res);
        setForm({
          checkIn: res.checkIn || '',
          checkOut: res.checkOut || '',
          rooms: res.rooms || {},
          roomsPrice: res.roomsPrice || {},
          roomsAvailable: res.roomsAvailable || {},
          mealOptions: res.mealOptions || {},
          selectedMeal: res.selectedMeal || '',
          discount: res.discount || 0,
          adults: res.adults || 1,
          children: res.children || 0,
          freeCancellation: res.freeCancellation || false
        });
      } catch (err) {
        console.error(err);
        alert('Eroare la încărcarea proprietății');
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId, bookingData]);

  // Nopti
  const nights = useMemo(() => {
    if (!form.checkIn || !form.checkOut) return 1;
    return Math.max(1, Math.ceil((new Date(form.checkOut) - new Date(form.checkIn)) / 86400000));
  }, [form.checkIn, form.checkOut]);

  // Total camera + masa
  const totalPrice = useMemo(() => {
    let total = 0;
    Object.keys(form.rooms).forEach(room => {
      const qty = Number(form.rooms[room] || 0);
      const price = Number(form.roomsPrice[room] || 0);
      total += qty * price * nights;
    });
    if (form.selectedMeal) {
      total += (form.mealOptions?.[form.selectedMeal] || 0) * nights;
    }
    return total;
  }, [form, nights]);

  const discountAmount = useMemo(() => {
    return form.discount > 0 ? parseFloat((totalPrice * (form.discount / 100)).toFixed(2)) : 0;
  }, [form.discount, totalPrice]);

  const finalPrice = useMemo(() => parseFloat((totalPrice - discountAmount).toFixed(2)), [totalPrice, discountAmount]);

  if (loading) return <div style={{ padding: '2rem' }}>Se încarcă...</div>;
  if (!property) return <div>Proprietatea nu a fost găsită.</div>;

  return (
    <div style={{ position: 'relative', maxWidth: '700px', margin: '0 auto', padding: '1rem', fontFamily: 'Sora, sans-serif', color: '#000' }}>
      
      {/* Buton Înapoi */}
      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: '1rem',
          background: 'transparent',
          border: 'none',
          color: '#dd4124',
          cursor: 'pointer',
          fontSize: '1.2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem'
        }}
      >
        ⇐
      </button>

      <h1 style={{ color: '#006d77' }}>Rezervă: {property.title}</h1>
      <p>{property.location}</p>

      {/* Sumar plata */}
      <div style={{ border: '1px solid #006d77', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#006d77' }}>Sumar plată</h2>
        <p>Nopți: {nights}</p>
        {Object.keys(form.rooms).map(room => (
          <p key={room}>{room}: {form.rooms[room]} x {form.roomsPrice[room]} RON/noapte</p>
        ))}
        {form.selectedMeal && <p>Masa: {form.selectedMeal} x {form.mealOptions[form.selectedMeal]} RON/noapte</p>}
        {form.discount > 0 && <p>Discount: -{form.discount}% (-{discountAmount} RON)</p>}
        <p>Adulți: {form.adults}, Copii: {form.children}</p>
        <p>Anulare gratuită: {form.freeCancellation ? 'Da' : 'Nu'}</p>
        <p style={{ fontWeight: 600 }}>Total: {finalPrice} RON</p>
      </div>

      {/* Checkout Stripe */}
      <div style={{ border: '1px solid #dd4124', borderRadius: '6px', padding: '1rem' }}>
        <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#dd4124' }}>Plata</h2>
        <Elements stripe={stripePromise}>
          <CheckoutForm finalPrice={finalPrice} propertyId={propertyId} user={user} form={form} />
        </Elements>
      </div>
    </div>
  );
}
